import { NOTEBOOK_EXPORT_LANGUAGE } from "./notebookAccessibleExport"

/**
 * Print a standalone document from a hidden frame. The frame declares the
 * export language, so a tagged print-to-PDF records it as the PDF's `/Lang`.
 * `fill` writes the content and may return a cleanup for after printing.
 */
export async function printInFrame(
  title: string,
  fill: (target: Document) => (() => void) | undefined,
) {
  // A browser that never sends afterprint leaves its frame until the next print.
  document
    .querySelectorAll("iframe[data-notebook-print-frame]")
    .forEach((previous) => previous.remove())
  const frame = document.createElement("iframe")
  frame.dataset.notebookPrintFrame = ""
  frame.title = "Notebook print preview"
  frame.style.cssText =
    "position:fixed;width:1px;height:1px;right:0;bottom:0;border:0;"
  document.body.append(frame)
  let cleanup: (() => void) | undefined
  const remove = () => {
    cleanup?.()
    cleanup = undefined
    frame.remove()
  }
  try {
    const target = frame.contentDocument
    const view = frame.contentWindow
    if (!target || !view) {
      throw new Error("Print preview could not open.")
    }
    target.documentElement.lang = NOTEBOOK_EXPORT_LANGUAGE
    target.title = title
    cleanup = fill(target)
    await Promise.all(
      Array.from(target.images).map((image) =>
        image.decode().catch(() => undefined),
      ),
    )
    await target.fonts.ready
    view.focus()
    // Browsers may return before the dialog closes. Keep its document alive until afterprint.
    view.addEventListener("afterprint", remove, { once: true })
    view.print()
  } catch (error) {
    remove()
    throw error
  }
}
